/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { mapFetchedDataToPortDetails } from "kontrolEdge/constants/constants";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ProvisioningDetailsModal, { SelectedPortSummary } from "kontrolEdge/components/Modals/ProvisioningDetails";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import RedundancySLAModal from "kontrolEdge/components/Modals/RedundancySLA";
import { useProcessingOverlay } from "@hooks/useProcessingOverlay";
import { ServiceDetailsPageSkeleton } from "kontrolEdge/components/Shared/ServiceDetailsPageSkeleton";
import GenericModal from "@ui-components/Modal/GenericModal";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import StatusTable from "./StatusTable";
import PortConnections from "./PortConnections/PortConnections";
import { usePortDetails } from "./usePortDetails";
import { Port } from "kontrolEdge/views/OverviewComponents/Types";
import { useAppSelector } from "@redux/hooks";
import { PortPerformanceData } from "@redux/slices/kontrolEdge/portSlice";
import BandwidthChartUniversal from "kontrolEdge/components/Shared/Bandwidth/BandwidthChartUniversal";
import PortDetailsCard from "./PortDetailsCard";
import { apiTagsToSummaries } from "kontrolEdge/components/Shared/NameTagEdit/useResourceTags";

export interface PortDetailItem {
  label: string;
  value?: string | number | boolean | React.ReactNode;
  type?: string;
  variant?: string;
  tooltip?: string;
}

export interface PortDetailSection {
  header: string;
  isCollapsible?: boolean;
  initialExpanded?: boolean;
  items: PortDetailItem[];
}

type LocationState = {
  selectedPortRow?: Partial<Pick<Port, "id" | "portId" | "customer" | "portType">>;
  portType?: "physical" | "virtual";
};

function getLatestInbound(pd?: PortPerformanceData | null) {
  const inbound = pd?.bandwidthUtilization?.inbound;
  if (!inbound?.length) return null;
  return inbound.reduce((prev, curr) =>
    new Date(curr.time).getTime() > new Date(prev.time).getTime() ? curr : prev
  );
}

const PortDetails: React.FC = () => {
  const [searchParams] = useSearchParams();
  //Port id selected from MainPort
  const portId = searchParams.get("portId");
  const subid = searchParams.get("subid") ?? undefined;
  const api = useAxiosAuth0();
  const portPerf = useAppSelector((s) => s.port.performanceData);
  const latestInbound = useMemo(() => getLatestInbound(portPerf), [portPerf]);


  const [refresh, setRefresh] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  // Data Layer: fetch core + extras
  const { raw, loading } = usePortDetails(
    portId,
    api,
    refresh
  );

  const location = useLocation();
  const { selectedPortRow, portType: statePortType } = (location.state as LocationState) ?? {};

  const fetchedPortType =
  (raw as any)?.portType ??
  (raw as any)?.kontrolEdgePort?.portType ??
  undefined;

  const effectivePortType = (statePortType ??
    selectedPortRow?.portType ??
    fetchedPortType) as "physical" | "virtual" | undefined;

  const selectedPortSummary = useMemo<SelectedPortSummary | null>(() => {
    const portIdStr =
      selectedPortRow?.portId ??
      (portId ?? undefined);
    if (!portIdStr) return null;
  
    const customerName =
      selectedPortRow?.customer?.name ??
      (raw as any)?.customer?.name ??
      undefined;
  
    return {
      portId: portIdStr,
      customer: customerName ? { name: customerName } : undefined,
    };
  }, [selectedPortRow, raw, portId]);

    // Derive detail sections
    const sections = useMemo(
      () =>
        raw
          ? mapFetchedDataToPortDetails(
              raw,
              selectedPortRow?.portId ?? undefined,
              subid
            )
          : [],
      [raw, selectedPortRow, subid]
    );

    // Rich tags inline from the detail GET (§5.5). Skips the §5.1 round-trip
    // when the new modal opens. If the response doesn't ship `tags` yet
    // (e.g. KD-563-2 not deployed in this env) the helper returns `[]` and
    // `useResourceTags` falls back to its own fetch.
    const initialAssignedTags = useMemo(
      () => apiTagsToSummaries((raw as { tags?: unknown } | null)?.tags),
      [raw]
    );

  //Edit name and tag inside the component
  const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);
  const [membersModalText, setMembersModalText] = useState("");
  const { tenantId } = useAppSelector((state) => state.selection);

    // Tenant the resource actually lives in (NOT the SideNav lens). Forwarded
    // to the Tags API so partner-view writes (`PUT /resources/.../tags`)
    // hit the right scope — the backend's resource-side adapter is strict on
    // `(id, tenantId)` and doesn't honor `getChildTenantIds`, so passing the
    // partner's own tenant via `useEffectiveTenantId()` 404s when the Port
    // belongs to a child. Always passing the resource tenant fixes both
    // partner and customer view in one go.
    //
    // Falls back to the SideNav lens tenant when `raw.tenantId` isn't yet
    // exposed (loading flicker, or backend regression) so the picker /
    // attach calls always carry an explicit `?tenantId=` and never resolve
    // to the JWT tenant by accident.
    const resourceTenantId = useMemo(() => {
      const t = (raw as { tenantId?: unknown } | null)?.tenantId;
      if (typeof t === "number" && Number.isFinite(t)) return t;
      return tenantId ?? undefined;
    }, [raw, tenantId]);

  // const resourceTenantId = useMemo(
  //   () => tenantId ?? undefined,
  //   [tenantId]
  // );

  const navigate = useNavigate();

  
  // Track the initial tenantId when component mounts to detect changes
  const initialTenantIdRef = useRef<number | null>(null);
  // Initialize the ref on mount
  useEffect(() => {
    if (initialTenantIdRef.current === null) {
      initialTenantIdRef.current = tenantId;
    }
  }, []);
  // Redirect to main view if tenantId changes while on detail page
  useEffect(() => {
    // Skip if ref hasn't been initialized yet
    if (initialTenantIdRef.current === null) return;
    // Only redirect if tenantId actually changed (not on initial mount)
    if (tenantId !== initialTenantIdRef.current) {
      navigate("/kontrol/port");
    }
  }, [tenantId, navigate]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [RedundancyOpen, setRedundancyOpen] = useState(false);

  const saveOverlay = useProcessingOverlay(isSaving);

  if (!portId) return <p>No port selected.</p>;
  if (loading && refresh === 0) {
    return <ServiceDetailsPageSkeleton variant="port" />;
  }
  if (!raw) return <p>Port not found.</p>;

  return (
    <>
      {saveOverlay}
      {/* Modals */}
      <ProvisioningDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        portDetailsData={sections}
        rawPortData={raw}
        selectedPort={selectedPortSummary}
      />
      <RedundancySLAModal
        isOpen={RedundancyOpen}
        onClose={() => setRedundancyOpen(false)}
        portDetailsData={sections}
        rawPortData={raw}
      />
      <GenericModal
        isOpen={isMembersModalOpen}
        onClose={() => setIsMembersModalOpen(false)}
        title="Members List"
        size="md"
      >
        <p className="text-sm text-semantic-text-secondary">
          {membersModalText}
        </p>
      </GenericModal>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4 auto-rows-auto">
        {sections.map((section, index) => {
          // optional right content per index (keep your existing behavior)
          const right =
            index === 0 ? (
              <GenericButton
                label="Provisioning Details"
                variant="secondary"
                shape="ghost"
                size="sm"
                onClick={() => setIsModalOpen(true)}
              />
            ) : index === 1 ? (
              <GenericTag
                label={
                  latestInbound
                    ? (() => {
                        const d = new Date(latestInbound.time);
                        const datePart = d.toLocaleDateString("es-MX", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          timeZone: "UTC",
                        });
                        const timePart = d.toLocaleTimeString("en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: false,
                          timeZone: "UTC",
                        });
                        return `${datePart} ${timePart} UTC`;
                      })()
                    : "-"
                }
              />
            ) : null;
          {/*Port detail cards section */}
          return (
            <div key={index} className="flex w-full self-start">
              <PortDetailsCard
                data={{
                  ...section,
                  isCollapsible: section.isCollapsible ?? index >= 2,
                }}
                rightContent={right}
                initialAssignedTags={initialAssignedTags}
                resourceTenantId={resourceTenantId}
              />
            </div>
          );
        })}
      </div>
      {/* Bandwidth Utilization chart */}
      <BandwidthChartUniversal
        service="port"
        id={portId}
        tenantId={tenantId}
        portType={effectivePortType}
      />
      {/* Associated connections section */}
      <PortConnections portId={portId} />
      {/* Activity Section */}
      <StatusTable
        data={raw?.statusHistory}
        headerContent="Activity"
        rightContent=""
      />
      {/* {canAccessAlerts && portId && (
        <NotificationsView serviceType="port" serviceId={portId} />
      )} */}
    </>
  );
};

export default PortDetails;
